"use client";
import jsonData from "@/public/data/hotel/detail.json";
import { Pulse } from "@/shared/components/common/Animations";
import { Button } from "@/shared/components/common/button";
import { CardFooter } from "@/shared/components/common/Card";
import Flex from "@/shared/components/common/Flex";
import List from "@/shared/components/common/List";
import Typography from "@/shared/components/common/Typography";
import type { MinPriceRoom } from "@/types/services/hotel/apis/Detail";
import { Door, Warning, WarningCircle } from "@phosphor-icons/react";
import Link from "next/link";

interface SidebarcardFooterProps {
  footerData: {
    img: string;
    title: string;
    stayNight: number;
    checkin: string;
    checkout: string;
    rooms: MinPriceRoom[];
    link?: string;
    description?: string;
    stars?: string;
  };
  loading?: boolean;
  page?: "Detail" | "Passengers" | "Confirm";

  onSubmit?: () => void;
}

export default function SidebarcardFooter({
  footerData: { rooms, link, stayNight },
  page = "Detail",
  loading,
  onSubmit,
}: SidebarcardFooterProps) {
  const {
    sidebarCard: {
      body: { currencyLabel },
      footer: { totalPrice, submits, rules, noRoomSelected },
    },
  } = jsonData;

  return (
    <CardFooter className="!p-0">
      <Flex className="w-full flex-col gap-y-4">
        {/* - * - * - Rules - * - * - */}

        {stayNight === 1 && (
          <Flex className="items-start justify-between gap-x-2 bg-primary-10 p-2  rounded-xs">
            <Warning weight="fill" className="w-6 h-6 text-primary-90" />
            <List
              items={rules}
              renderItems={(item) => (
                <Typography
                  variant="body1"
                  className=" text-primary-90  !leading-6"
                >
                  {item}
                </Typography>
              )}
            />
          </Flex>
        )}

        {rooms.length === 0 ? (
          <Flex className="justify-center items-center gap-x-2">
            <WarningCircle className="w-6 h-6 text-primary self-start" />
            <Typography variant="body3" className="font-normal">
              {noRoomSelected}
            </Typography>
          </Flex>
        ) : (
          <Flex className="flex-col">
            <Flex className="justify-between py-3 px-1 rounded-xs">
              <Typography variant="body1" className="!font-semibold">
                {totalPrice}
              </Typography>

              <Flex className="items-center gap-x-1">
                <Typography
                  variant="body1"
                  className="text-primary !font-semibold"
                >
                  {rooms
                    .reduce(
                      (prev, curr) => prev + curr.rate_plans.price * curr.count,
                      0,
                    )
                    .toLocaleString()}
                </Typography>
                <Typography
                  variant="body1"
                  className="text-ghost font-medium text-sm"
                >
                  {currencyLabel}
                </Typography>
              </Flex>
            </Flex>

            {/* - * - * - Submit Button - * - * - */}
            {link ? (
              <Link href={link} className="w-full">
                <Button
                  onClick={onSubmit}
                  type="submit"
                  className="bg-primary rounded-xs! w-full py-6 hover:bg-transparent shadow-none border-2 border-primary hover:text-primary!"
                >
                  {loading ? <Pulse /> : submits[page]}
                </Button>
              </Link>
            ) : (
              <>
                <Button
                  onClick={onSubmit}
                  type="submit"
                  className="bg-primary rounded-xs! w-full py-6 hover:bg-transparent shadow-none border-2 border-primary hover:text-primary!"
                >
                  {loading ? <Pulse /> : submits[page]}
                </Button>
              </>
            )}
          </Flex>
        )}
      </Flex>
    </CardFooter>
  );
}
